<template>
  <el-dialog
    v-model="visible"
    class="ai-init-setup-dialog"
    :close-on-click-modal="false"
    destroy-on-close
    :show-close="false"
  >
    <template #default>
      <div style="height: 60px; width: 100%"></div>
      <div class="ai-init-setup-dialog__body">
        <div class="head">
          <div class="head__text">
            <div class="head__title">轻松 5 步让 AI 更好用</div>
            <div class="head__desc">补充基础配置，让我更懂您，以便更好的为您工作~</div>
          </div>
          <i class="head__icon icon-collapse-diagonal-line t-iconfont" @click="close"></i>
        </div>
        <AiInitUninitialized
          v-if="!isAiInitialized && !isAiInitializing"
          @generate="handleGenerate"
        />
        <AiInitInitializing v-else-if="!isAiInitialized && isAiInitializing" />
        <div v-else class="content">
          <div class="setup-tabs">
            <div
              v-for="(item, index) in setupTabs"
              :key="item.title"
              class="setup-tab"
              :class="{
                'setup-tab--active': item.alias === activeTabAlias,
                'setup-tab--done': item.isComplete === 1
              }"
              @click="handleClickTab(item)"
            >
              <i
                v-if="item.isComplete === 1"
                class="icon-checkbox-circle-fill setup-tab__done-icon t-iconfont"
              ></i>
              <span
                v-else
                class="setup-tab__badge"
                :class="{ 'setup-tab__badge--active': item.alias === activeTabAlias }"
              >
                {{ index + 1 }}
              </span>
              <div class="setup-tab__text">
                <div class="setup-tab__title">{{ item.title }}</div>
                <div class="setup-tab__desc">{{ item.desc }}</div>
              </div>
            </div>
          </div>
          <div v-if="!isAllComplete" class="setup-panel">
            <div class="setup-panel__content">
              <template v-if="activeTabAlias === 'aiLicenseConfig'">
                <AiAssistantPermission is-init />
              </template>
              <template v-else-if="activeTabAlias === 'companyDescriptionConfig'">
                <StaffPracticePartnerConfiguration
                  ref="staffPracticePartnerConfigurationRef"
                  is-init
                />
              </template>
              <template v-else-if="activeTabAlias === 'followUpDataManagementConfig'">
                <DataSourceManagement is-init />
              </template>
              <template v-else-if="activeTabAlias === 'followUpAssistantConfig'">
                <AiFollowUpAssistantConfig
                  :is-out-last="isLastIncompletePrompt"
                  @saveSuccess="handleConfirmSetup"
                />
              </template>
              <template v-else-if="activeTabAlias === 'assistantPermissionConfig'">
                <AssistantManagement is-init />
              </template>
            </div>
            <el-button
              v-if="showConfirmButton"
              class="setup-panel__confirm"
              color="#7a52f4"
              :loading="confirmLoading"
              type="primary"
              @click="handleConfirmSetup"
            >
              {{ confirmButtonText }}
            </el-button>
          </div>
          <div v-else class="success">
            <AiInitSetupSuccess @explore="close" />
          </div>
        </div>
      </div>
      <div style="height: 60px; width: 100%"></div>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import AiAssistantPermission from '@/pages/ai-board/permission-management/components/ai-assistant-permission.vue'
import StaffPracticePartnerConfiguration from '@/pages/ai-board/staff-practice-partner-configuration/index.vue'
import DataSourceManagement from '@/pages/ai-board/data-source-management/index.vue'
import AssistantManagement from '@/pages/ai-board/assistant-management/index.vue'
import AiFollowUpAssistantConfig from './AiFollowUpAssistantConfig.vue'
import AiInitSetupSuccess from './AiInitSetupSuccess.vue'
import AiInitUninitialized from './AiInitUninitialized.vue'
import AiInitInitializing from './AiInitInitializing.vue'
import { useAppStore } from '@/store/useAppStore'
import { editInitialization, checkInitializationComplete, autoInitialization } from '@/api/index'
import AppMessage from '@/components/message/index.js'
import emitter from '@/utils/mitt'
import utils from '@/utils'
import { getInitSetupList } from './init-setup'

const appStore = useAppStore()
const visible = ref(false)
const activeTabAlias = ref('companyDescriptionConfig')
const confirmLoading = ref(false)
const staffPracticePartnerConfigurationRef = ref()
const isAiInitialized = computed(() => appStore.isAiInitialization === 4)
const isAiInitializing = computed(() => [1, 2, 3].includes(appStore.isAiInitialization))
let statusTimer = null
let nullStatusCount = 0
const maxNullStatusCount = 5

const setupTabs = computed(() => getInitSetupList(appStore.aiInitConfigDetails))
const showConfirmButton = computed(() => activeTabAlias.value !== 'followUpAssistantConfig')

// 是否全部完成
const isAllComplete = computed(() => setupTabs.value.every((item) => item.isComplete === 1))

// 是否当前步骤是最后一个未完成的步骤
const isLastIncompletePrompt = computed(() => {
  const incompleteDetails = setupTabs.value.filter((item) => item.isComplete !== 1)
  return incompleteDetails.length === 1 && incompleteDetails[0]?.alias === activeTabAlias.value
})

const confirmButtonText = computed(() => {
  return isLastIncompletePrompt.value ? '完成' : '已完成此步骤配置'
})

const clearStatusTimer = () => {
  clearTimeout(statusTimer)
  statusTimer = null
}

const getStatus = () => {
  console.log('getStatus', appStore.isAiInitialization)
  const status = appStore.isAiInitialization

  if (status === null || status === undefined) {
    nullStatusCount += 1
    if (nullStatusCount >= maxNullStatusCount) {
      clearStatusTimer()
      return
    }
  } else {
    nullStatusCount = 0
  }

  if ([0, 4].includes(status) || appStore.aiInitIsCompleted === 1) {
    clearStatusTimer()
    return
  }

  emitter.emit('getInitializationConfig')
  clearStatusTimer()
  statusTimer = setTimeout(() => {
    getStatus()
  }, 2000)
}

const handleGenerate = async (name) => {
  appStore.setAiInitialization(1)
  try {
    await autoInitialization({ companyName: name })
    getStatus()
  } catch {
    // 接口失败时保持当前状态不变。
  }
}

const handleClickTab = (item) => {
  if (item.isComplete === 1) return
  activeTabAlias.value = item.alias
}

const getUpdatedConfigDetails = (alias, userId, operateTime) => {
  return appStore.aiInitConfigDetails.map((item) => {
    if (item.configAlias !== alias) return { ...item }

    return {
      ...item,
      isComplete: 1,
      operator: userId,
      operateTime
    }
  })
}

const switchToNextIncompleteStep = (configDetails) => {
  const updatedTabs = getInitSetupList(configDetails)
  const currentIndex = updatedTabs.findIndex((item) => item.alias === activeTabAlias.value)
  const nextStep = updatedTabs.slice(currentIndex + 1).find((item) => item.isComplete !== 1)
  const previousStep = updatedTabs.slice(0, currentIndex).find((item) => item.isComplete !== 1)

  if (nextStep || previousStep) {
    activeTabAlias.value = (nextStep || previousStep).alias
  }
}

const saveCurrentStep = async () => {
  if (activeTabAlias.value === 'companyDescriptionConfig') {
    await staffPracticePartnerConfigurationRef.value?.toSave()
  }
}

const handleConfirmSetup = async () => {
  if (confirmLoading.value) return

  if (activeTabAlias.value === 'companyDescriptionConfig') {
    const res = await checkInitializationComplete({ configAlias: 'companyDescriptionConfig' })
    if (res?.result?.isCompleted === 1) {
      AppMessage.info(`该步配置已被「${res?.result?.operatorName}」完成`)
      emitter.emit('getInitializationConfig')
      return
    }
  }

  confirmLoading.value = true
  try {
    await saveCurrentStep()
    const _isLastIncompletePrompt = isLastIncompletePrompt.value
    const operateTime = Math.floor(Date.now() / 1000)
    const userId = utils.xbbLocalStorageGet('userId') || ''
    const configDetails = getUpdatedConfigDetails(activeTabAlias.value, userId, operateTime)

    await editInitialization({ configDetails })
    appStore.setAiInitConfigDetails(configDetails)
    if (!_isLastIncompletePrompt) {
      switchToNextIncompleteStep(configDetails)
    } else {
      appStore.setAiInitIsCompleted(1)
    }
  } catch {
    // 接口失败时保持 store 原值不变。
  } finally {
    confirmLoading.value = false
  }
}

const open = (alias) => {
  if (alias) {
    activeTabAlias.value = alias
  }
  visible.value = true
}

const close = () => {
  visible.value = false
  if (appStore.isPopup === 1) {
    emitter.emit('openAiMenuTour')
  }
}

onMounted(() => {
  console.log('---组件挂载---获取初始化配置')
  getStatus()
})

onBeforeUnmount(() => {
  clearStatusTimer()
})

defineExpose({ open, close })
</script>

<style lang="scss">
.el-overlay:has(.ai-init-setup-dialog) {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  .el-overlay-dialog {
    display: flex;
    align-items: center;
    justify-content: center;
    position: static;
    width: 100%;
    height: 100%;
    padding: 0 200px;
  }

  .ai-init-setup-dialog.el-dialog {
    width: 100%;
    height: 100%;
    max-width: 1008px;
    max-height: 920px;
    margin: 0;
    position: relative;
    border-radius: 24px;
    background: unset;
    .el-dialog__header {
      border-bottom: none;
    }
    .el-dialog__body {
      padding: 0;
      height: 100%;
      width: 100%;
      min-height: 720px;
      border-radius: 24px;
      overflow: hidden;
    }
  }
}
</style>

<style lang="scss" scoped>
.ai-init-setup-dialog__body {
  width: 100%;
  height: calc(100% - 120px);
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  min-height: 0;
  background:
    linear-gradient(243deg, rgba(241, 223, 255, 0.5) 1%, rgba(255, 255, 255, 0.5) 14%),
    linear-gradient(193deg, rgba(182, 206, 255, 0.5) 18%, rgba(255, 255, 255, 0.5) 25%), #fff;
  border: 2px solid #fff;
  border-radius: 24px;
}

.head {
  width: 100%;
  height: 60px;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.content {
  width: 100%;
  flex: 1;
  margin-top: 24px;
  display: flex;
  overflow: hidden;
  min-height: 0;
}

.head__text {
  flex: 1;
}

.head__title {
  width: 237px;
  height: 34px;
  font-family: 'PingFang SC', sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 34px;
  color: transparent;
  background: linear-gradient(278deg, #090120 0%, #4f16c1 100%);
  background-clip: text;
}

.head__desc {
  height: 22px;
  margin-top: 4px;
  font-family: 'PingFang SC', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: #646566;
}

.head__icon {
  width: 16px;
  height: 16px;
  margin-left: 16px;
  font-size: 16px;
  line-height: 16px;
  color: #969799;
  cursor: pointer;
}

.setup-tabs {
  width: 220px;
  height: 100%;
  flex-shrink: 0;
}

.setup-tab {
  width: 220px;
  height: 70px;
  padding: 12px 16px 12px 12px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  border-radius: 16px;
  cursor: pointer;
}

.setup-tab--active {
  background: #f5e8ff;
}

.setup-tab--done {
  cursor: not-allowed;
}

.setup-tab--done .setup-tab__text {
  opacity: 0.5;
}

.setup-tab__badge {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin: 0 10px 0 2px;
  border-radius: 50%;
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
  color: #fff;
  background: #c9cdd4;
}

.setup-tab__badge--active {
  color: #7a52f4;
  background: #fff;
}

.setup-tab__done-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-right: 8px;
  font-size: 22px;
  line-height: 24px;
  color: #00c241;
}

.setup-tab__text {
  flex: 1;
  min-width: 0;
}

.setup-tab__title {
  height: 22px;
  font-family: 'PingFang SC', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  color: #323233;
  white-space: nowrap;
}

.setup-tab__desc {
  height: 20px;
  margin-top: 4px;
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #969799;
  white-space: nowrap;
}

.setup-panel {
  height: 100%;
  flex: 1;
  margin-left: 12px;
  box-sizing: border-box;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 16px;
  overflow: hidden;
}

.success {
  height: 100%;
  flex: 1;
  margin-left: 12px;
  overflow: hidden;
}

.setup-panel__content {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.setup-panel__confirm {
  width: 144px;
  height: 32px;
  flex: 0 0 auto;
  margin-top: 12px;
  padding: 6px 16px;
  border-radius: 8px;
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}
</style>
